import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3203';
import {
  buildDefaultPromptsBody,
  doReplaceDefaultPrompts,
  luHandleAppiError,
  normalizeU3203AiMissionFromApi,
  type U3203AiMission,
  type U3203AiMissionFormRow,
  type U3203DefaultPromptItemInput,
} from '../U3203Service';

export function useU3203DefaultPromptsHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const replaceDefaultPrompts = useCallback(
    async (missionId: string, items: U3203DefaultPromptItemInput[]): Promise<U3203AiMissionFormRow | null> => {
      setSaving(true);
      try {
        const response = await doReplaceDefaultPrompts(
          u2000AxiosAuthenticated,
          missionId,
          buildDefaultPromptsBody(items),
        );
        const row = response.data.result as U3203AiMission;
        const normalized = normalizeU3203AiMissionFromApi(row);
        toast.success(t('msgDefaultPromptsSaved', { ns: NS }), t('successTitle', { ns: NS }));
        return normalized;
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [handleApiError, t, toast, u2000AxiosAuthenticated],
  );

  return { replaceDefaultPrompts, saving };
}
